import { useEffect, useState } from "react";
import { Download, BarChart3, LifeBuoy, ScrollText, Plug, CheckCircle2, CircleDashed, ExternalLink, Send, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { useApi, errMsg } from "@/lib/api";
import { fmtDT } from "@/lib/brand";
import { DateTimeField } from "@/components/site/DateTimeField";
import { TICKET_STYLE } from "@/pages/Support";

const today = () => new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
const monthStart = () => `${today().slice(0, 8)}01`;
const input = "w-full rounded-xl border border-hc-sage bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-hc-green";

function Head({ icon: Icon, title, sub }) {
  return (
    <div className="mb-6">
      <h1 className="flex items-center gap-3 font-display text-3xl font-extrabold tracking-tight"><Icon className="h-7 w-7 text-hc-green" />{title}</h1>
      {sub && <p className="mt-1 text-sm text-hc-slate">{sub}</p>}
    </div>
  );
}

const REPORTS = [["gst", "GST sales register", "Invoice-wise taxable value, CGST 9% and SGST 9% for GSTR-1 filing."], ["bookings", "Bookings report", "Every booking created in the period with status, payment and amounts."], ["refunds", "Refunds & deposits", "Cancellation refunds and security-deposit refunds with their status."]];

export function AdminReports() {
  const api = useApi();
  const [start, setStart] = useState(monthStart());
  const [end, setEnd] = useState(today());
  const [busy, setBusy] = useState("");
  const download = async (kind) => {
    setBusy(kind);
    try {
      const r = await api.get(`/admin/reports/${kind}.csv`, { params: { start: start.slice(0, 10), end: end.slice(0, 10) }, responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data);
      a.download = `hoorecars-${kind}-${start.slice(0, 10)}-to-${end.slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) {
      toast.error(e?.response?.status === 422 ? "Check the dates — end must be after start" : errMsg(e));
    } finally {
      setBusy("");
    }
  };
  return (
    <div data-testid="admin-reports">
      <Head icon={BarChart3} title="Reports & GST" sub="Download CSV reports (opens in Excel / Google Sheets). Dates are in IST." />
      <div className="card-hc mb-6 grid gap-4 p-5 sm:grid-cols-2 lg:max-w-xl">
        <label className="text-xs font-semibold uppercase tracking-wide text-hc-slate">From<DateTimeField withTime={false} allowPast value={start} onChange={setStart} className={`${input} mt-1`} testId="report-start" /></label>
        <label className="text-xs font-semibold uppercase tracking-wide text-hc-slate">To<DateTimeField withTime={false} allowPast value={end} onChange={setEnd} className={`${input} mt-1`} testId="report-end" /></label>
      </div>
      <div className="grid gap-4 md:grid-cols-3">
        {REPORTS.map(([k, t, d]) => (
          <div key={k} className="card-hc flex flex-col p-5" data-testid={`report-${k}`}>
            <p className="font-display text-lg font-bold">{t}</p>
            <p className="mt-1 flex-1 text-sm text-hc-slate">{d}</p>
            <button onClick={() => download(k)} disabled={!!busy} className="btn-primary mt-4" data-testid={`report-download-${k}`}><Download className="h-4 w-4" />{busy === k ? "Preparing…" : "Download CSV"}</button>
          </div>
        ))}
      </div>
    </div>
  );
}

function TicketDetail({ t, onSaved }) {
  const api = useApi();
  const [msg, setMsg] = useState("");
  const act = async (fn) => {
    try {
      onSaved((await fn()).data);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="card-hc p-5" data-testid={`admin-ticket-${t.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="font-semibold">{t.subject}</p>
          <p className="text-xs text-hc-slate">{t.id} · {t.category} · {t.name} ({t.email}){t.booking_id ? ` · Booking ${t.booking_id}` : ""}</p>
        </div>
        <select value={t.status} onChange={(e) => act(() => api.patch(`/admin/tickets/${t.id}`, { status: e.target.value }))} className="rounded-lg border border-hc-sage bg-white px-2 py-1 text-xs" data-testid={`admin-ticket-status-${t.id}`}>
          {Object.keys(TICKET_STYLE).map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
        </select>
      </div>
      <div className="mt-4 max-h-72 space-y-2 overflow-y-auto">
        {t.messages.map((m, i) => (
          <div key={i} className={`max-w-[85%] rounded-xl px-3.5 py-2 text-sm ${m.from === "support" ? "ml-auto bg-hc-green text-white" : "bg-hc-mint text-hc-ink"}`}>
            <p className="whitespace-pre-wrap">{m.message}</p><p className="mt-1 text-[11px] opacity-70">{m.from === "support" ? m.by || "Support" : m.name || "Customer"} · {fmtDT(m.at)}</p>
          </div>
        ))}
      </div>
      <form onSubmit={(e) => { e.preventDefault(); act(() => api.post(`/admin/tickets/${t.id}/messages`, { message: msg })).then(() => setMsg("")); }} className="mt-3 flex gap-2">
        <input value={msg} onChange={(e) => setMsg(e.target.value)} required placeholder="Reply to customer" className={input} data-testid={`admin-ticket-reply-${t.id}`} />
        <button className="btn-primary !px-4" aria-label="Send reply" data-testid={`admin-ticket-send-${t.id}`}><Send className="h-4 w-4" /></button>
      </form>
    </div>
  );
}

export function AdminTickets() {
  const api = useApi();
  const [status, setStatus] = useState("");
  const [list, setList] = useState(null);
  useEffect(() => {
    api.get("/admin/tickets", { params: status ? { status } : {} }).then((r) => setList(r.data)).catch(() => setList([]));
  }, [api, status]);
  const saved = (t) => setList((cur) => cur.map((x) => (x.id === t.id ? t : x)));
  return (
    <div data-testid="admin-tickets">
      <Head icon={LifeBuoy} title="Support tickets" sub="Customer tickets raised from the website and app." />
      <div className="mb-5 flex flex-wrap gap-2">
        {["", ...Object.keys(TICKET_STYLE)].map((s) => (
          <button key={s || "all"} onClick={() => setStatus(s)} data-testid={`tickets-filter-${s || "all"}`}
            className={`rounded-full border px-3.5 py-1.5 text-sm ${status === s ? "border-hc-green bg-hc-green !text-white" : "border-hc-sage bg-white"}`}>{s ? s.replace("_", " ") : "All"}</button>
        ))}
      </div>
      {list === null ? <div className="skeleton h-32" /> : list.length === 0 ? <p className="rounded-2xl border border-dashed border-hc-sage bg-white p-10 text-center text-hc-slate" data-testid="admin-tickets-empty">No tickets.</p>
        : <div className="grid gap-4 xl:grid-cols-2">{list.map((t) => <TicketDetail key={t.id} t={t} onSaved={saved} />)}</div>}
    </div>
  );
}

export function AdminAudit() {
  const api = useApi();
  const [rows, setRows] = useState(null);
  useEffect(() => {
    api.get("/admin/audit").then((r) => setRows(r.data)).catch(() => setRows([]));
  }, [api]);
  return (
    <div data-testid="admin-audit">
      <Head icon={ScrollText} title="Audit logs" sub="Every successful change made in the admin panel (latest 200)." />
      <div className="overflow-x-auto rounded-2xl border border-hc-sage bg-white">
        <table className="w-full min-w-[640px] text-sm">
          <thead className="bg-hc-mint text-left text-xs uppercase tracking-wide text-hc-slate"><tr><th className="p-3">When</th><th className="p-3">Admin</th><th className="p-3">Action</th><th className="p-3">Path</th><th className="p-3">IP</th></tr></thead>
          <tbody className="divide-y divide-hc-sage">
            {rows === null && <tr><td colSpan={5} className="p-6"><div className="skeleton h-6" /></td></tr>}
            {rows?.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-hc-slate" data-testid="audit-empty">No admin actions recorded yet.</td></tr>}
            {rows?.map((r) => (
              <tr key={r.id} data-testid={`audit-row-${r.id}`}><td className="whitespace-nowrap p-3">{fmtDT(r.created_at)}</td><td className="p-3">{r.email || r.user_id || "—"}</td>
                <td className="p-3"><span className="rounded-md bg-hc-mint px-2 py-0.5 font-mono text-xs font-semibold text-hc-green">{r.method}</span></td><td className="p-3 font-mono text-xs">{r.path}</td><td className="p-3 text-xs text-hc-slate">{r.ip}</td></tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const FRONTEND_KEYS = {
  REACT_APP_FIREBASE_API_KEY: process.env.REACT_APP_FIREBASE_API_KEY,
  REACT_APP_FIREBASE_APP_ID: process.env.REACT_APP_FIREBASE_APP_ID,
  REACT_APP_GOOGLE_MAPS_API_KEY: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
};

function IntegrationCard({ i, onTested }) {
  const api = useApi();
  const [busy, setBusy] = useState(false);
  const configured = i.frontend ? i.keys.every((k) => FRONTEND_KEYS[k]) : i.configured;
  const test = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/integrations/${i.id}/test`);
      r.data.ok ? toast.success(r.data.message) : toast.error(r.data.message);
      onTested(i.id, r.data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="card-hc flex flex-col p-5" data-testid={`integration-${i.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div><p className="text-[11px] font-semibold uppercase tracking-wide text-hc-goldh">{i.category}</p><p className="font-display text-lg font-bold">{i.name}</p></div>
        <span className={`flex shrink-0 items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${configured ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`} data-testid={`integration-status-${i.id}`}>
          {configured ? <CheckCircle2 className="h-3.5 w-3.5" /> : <CircleDashed className="h-3.5 w-3.5" />}{i.managed ? "Active" : configured ? "Keys added" : "Not connected"}</span>
      </div>
      <p className="mt-3 flex-1 text-sm text-hc-slate">{i.how}</p>
      {i.keys.length > 0 && <p className="mt-3 flex flex-wrap gap-1.5">{i.keys.map((k) => <code key={k} className={`rounded-md px-1.5 py-0.5 text-[10px] ${(i.frontend ? FRONTEND_KEYS[k] : i.keys_present.includes(k)) ? "bg-hc-mint text-hc-green" : "bg-neutral-100 text-hc-slate"}`}>{k}</code>)}</p>}
      {i.last_check && <p className={`mt-3 text-xs ${i.last_check.ok ? "text-hc-success" : "text-hc-error"}`} data-testid={`integration-check-${i.id}`}>Last test {fmtDT(i.last_check.at)}: {i.last_check.message}</p>}
      <div className="mt-4 flex flex-wrap gap-2">
        {i.testable && <button onClick={test} disabled={busy} className="btn-outline !py-1.5" data-testid={`integration-test-${i.id}`}><RefreshCw className={`h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} />Test connection</button>}
        {i.url && <a href={i.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 px-2 py-1.5 text-sm font-semibold text-hc-green" data-testid={`integration-link-${i.id}`}>Get keys <ExternalLink className="h-3.5 w-3.5" /></a>}
      </div>
    </div>
  );
}

export function AdminIntegrations() {
  const api = useApi();
  const [list, setList] = useState(null);
  useEffect(() => {
    api.get("/admin/integrations").then((r) => setList(r.data)).catch(() => setList([]));
  }, [api]);
  const tested = (id, rec) => setList((cur) => cur.map((x) => (x.id === id ? { ...x, last_check: rec } : x)));
  return (
    <div data-testid="admin-integrations">
      <Head icon={Plug} title="Integrations" sub="Connection status of each provider. Secret values are never shown — keys are stored securely on the server and added through the secure keys card." />
      {list === null ? <div className="skeleton h-40" /> : <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">{list.map((i) => <IntegrationCard key={i.id} i={i} onTested={tested} />)}</div>}
    </div>
  );
}
